'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeroSlots } from './useHeroSlots';
import { useCache } from '@effektify/cache';
import { useDataProvider } from '@effektify/data-provider';
import { useMemo, useState } from 'react';
import useIntegrations from 'src/hooks/useIntegrations';
import { deriveHeroState, hasDatasetValues, toHeroConnections } from './utils';
const getSlotSources = ({
  kpis,
  gauge,
  chart
}: HeroSlots) => [...new Set([...kpis, gauge, chart].flatMap(card => Object.values(card?.data?.sources ?? {}).map(({
  source
}) => source)))];

/**
 * "No data" is only concluded once the report response and every follow-up fetch it
 * announced have settled, and the datasets the hero cards read from are all empty.
 */
const useHeroState = slots => {
  const $ = _c(12);
  const {
    isConfigLoading,
    isDataSettled,
    isSubsequentDataPending
  } = useDataProvider();
  const {
    integrations,
    adapters,
    isLoadingIntegrations
  } = useIntegrations();
  const {
    getData
  } = useCache();
  let t0;
  if ($[0] !== integrations) {
    t0 = toHeroConnections(integrations);
    $[0] = integrations;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const connections = t0;
  let t1;
  if ($[2] !== slots) {
    t1 = getSlotSources(slots);
    $[2] = slots;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const sources = t1;
  let t2;
  if ($[4] !== getData) {
    t2 = source => hasDatasetValues(getData(source).data);
    $[4] = getData;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const hasCachedData = sources.some(t2);
  const [hadData, setHadData] = useState(false);
  if (hasCachedData && !hadData) {
    setHadData(true);
  }
  if (isDataSettled && !hasCachedData && hadData) {
    setHadData(false);
  }
  const hasAnyData = hasCachedData || hadData && !isDataSettled;
  const heroState = deriveHeroState({
    isLoading: isLoadingIntegrations || isConfigLoading,
    connections,
    hasAnyData,
    isDataSettled
  });
  let t3;
  if ($[6] !== adapters || $[7] !== connections || $[8] !== heroState || $[9] !== isDataSettled || $[10] !== isSubsequentDataPending) {
    t3 = {
      heroState,
      connections,
      adapters,
      isDataSettled,
      isSubsequentDataPending
    };
    $[6] = adapters;
    $[7] = connections;
    $[8] = heroState;
    $[9] = isDataSettled;
    $[10] = isSubsequentDataPending;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export default useHeroState;